import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  Announcement,
  AnnouncementInput,
  FavoriteLink,
  FavoriteLinkInput,
  FavoriteLinkOrderInput,
  Newsletter,
  NewsletterSyncResult,
  ShortcutFavorite,
  ShortcutFavoriteInput,
  Tip,
  TipInput,
  UserShortcutLink,
  UserShortcutLinkInput,
} from '@internal/plugin-management-common';

export interface ManagementApi {
  listAnnouncements(): Promise<Announcement[]>;
  createAnnouncement(input: AnnouncementInput): Promise<Announcement>;
  updateAnnouncement(
    id: number,
    input: AnnouncementInput,
  ): Promise<Announcement>;
  deleteAnnouncement(id: number): Promise<void>;
  listTips(): Promise<Tip[]>;
  createTip(input: TipInput): Promise<Tip>;
  updateTip(id: number, input: TipInput): Promise<Tip>;
  deleteTip(id: number): Promise<void>;
  listNewsletters(includeDrafts?: boolean): Promise<Newsletter[]>;
  getNewsletter(slug: string): Promise<Newsletter>;
  getNewsletterAssetUrl(slug: string, assetPath: string): Promise<string>;
  syncNewsletters(): Promise<NewsletterSyncResult>;
  likeNewsletter(slug: string): Promise<Newsletter>;
  unlikeNewsletter(slug: string): Promise<Newsletter>;
  listFavoriteLinks(): Promise<FavoriteLink[]>;
  createFavoriteLink(input: FavoriteLinkInput): Promise<FavoriteLink>;
  updateFavoriteLink(
    id: number,
    input: FavoriteLinkInput,
  ): Promise<FavoriteLink>;
  reorderFavoriteLinks(
    items: FavoriteLinkOrderInput[],
  ): Promise<FavoriteLink[]>;
  deleteFavoriteLink(id: number): Promise<void>;
  listShortcutFavorites(): Promise<ShortcutFavorite[]>;
  createShortcutFavorite(
    input: ShortcutFavoriteInput,
  ): Promise<ShortcutFavorite>;
  deleteShortcutFavorite(shortcutId: string): Promise<void>;
  listUserShortcutLinks(): Promise<UserShortcutLink[]>;
  createUserShortcutLink(
    input: UserShortcutLinkInput,
  ): Promise<UserShortcutLink>;
  updateUserShortcutLink(
    id: number,
    input: UserShortcutLinkInput,
  ): Promise<UserShortcutLink>;
  deleteUserShortcutLink(id: number): Promise<void>;
}

export const managementApiRef = createApiRef<ManagementApi>({
  id: 'plugin.management.service',
});

export class ManagementClient implements ManagementApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('management');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...options,
      headers: {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(
        `Management request failed: ${await this.getErrorMessage(response)}`,
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  private async getErrorMessage(response: Response): Promise<string> {
    const fallback = response.statusText || `HTTP ${response.status}`;
    const body = await response.text();

    if (!body) {
      return fallback;
    }

    try {
      const payload = JSON.parse(body) as {
        error?: { message?: string } | string;
        message?: string;
      };
      if (typeof payload.error === 'string') {
        return payload.error;
      }
      return payload.error?.message || payload.message || fallback;
    } catch {
      return body;
    }
  }

  listAnnouncements(): Promise<Announcement[]> {
    return this.request('/announcements');
  }

  createAnnouncement(input: AnnouncementInput): Promise<Announcement> {
    return this.request('/announcements', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  updateAnnouncement(
    id: number,
    input: AnnouncementInput,
  ): Promise<Announcement> {
    return this.request(`/announcements/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  deleteAnnouncement(id: number): Promise<void> {
    return this.request(`/announcements/${id}`, {
      method: 'DELETE',
    });
  }

  listTips(): Promise<Tip[]> {
    return this.request('/tips');
  }

  createTip(input: TipInput): Promise<Tip> {
    return this.request('/tips', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  updateTip(id: number, input: TipInput): Promise<Tip> {
    return this.request(`/tips/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  deleteTip(id: number): Promise<void> {
    return this.request(`/tips/${id}`, {
      method: 'DELETE',
    });
  }

  listNewsletters(includeDrafts = false): Promise<Newsletter[]> {
    return this.request(
      includeDrafts ? '/newsletters?includeDrafts=true' : '/newsletters',
    );
  }

  getNewsletter(slug: string): Promise<Newsletter> {
    return this.request(`/newsletters/${encodeURIComponent(slug)}`);
  }

  async getNewsletterAssetUrl(
    slug: string,
    assetPath: string,
  ): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('management');
    const encodedPath = assetPath
      .replace(/^\.\//, '')
      .split('/')
      .map(encodeURIComponent)
      .join('/');
    return `${baseUrl}/newsletters/${encodeURIComponent(
      slug,
    )}/assets/${encodedPath}`;
  }

  syncNewsletters(): Promise<NewsletterSyncResult> {
    return this.request('/newsletters/sync', { method: 'POST' });
  }

  likeNewsletter(slug: string): Promise<Newsletter> {
    return this.request(`/newsletters/${encodeURIComponent(slug)}/like`, {
      method: 'PUT',
    });
  }

  unlikeNewsletter(slug: string): Promise<Newsletter> {
    return this.request(`/newsletters/${encodeURIComponent(slug)}/like`, {
      method: 'DELETE',
    });
  }

  listFavoriteLinks(): Promise<FavoriteLink[]> {
    return this.request('/favorite-links');
  }

  createFavoriteLink(input: FavoriteLinkInput): Promise<FavoriteLink> {
    return this.request('/favorite-links', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  updateFavoriteLink(
    id: number,
    input: FavoriteLinkInput,
  ): Promise<FavoriteLink> {
    return this.request(`/favorite-links/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  reorderFavoriteLinks(
    items: FavoriteLinkOrderInput[],
  ): Promise<FavoriteLink[]> {
    return this.request('/favorite-links/order', {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
  }

  deleteFavoriteLink(id: number): Promise<void> {
    return this.request(`/favorite-links/${id}`, {
      method: 'DELETE',
    });
  }

  listShortcutFavorites(): Promise<ShortcutFavorite[]> {
    return this.request('/shortcut-favorites');
  }

  createShortcutFavorite(
    input: ShortcutFavoriteInput,
  ): Promise<ShortcutFavorite> {
    return this.request('/shortcut-favorites', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  deleteShortcutFavorite(shortcutId: string): Promise<void> {
    return this.request(
      `/shortcut-favorites/${encodeURIComponent(shortcutId)}`,
      {
        method: 'DELETE',
      },
    );
  }

  listUserShortcutLinks(): Promise<UserShortcutLink[]> {
    return this.request('/user-shortcut-links');
  }

  createUserShortcutLink(
    input: UserShortcutLinkInput,
  ): Promise<UserShortcutLink> {
    return this.request('/user-shortcut-links', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  updateUserShortcutLink(
    id: number,
    input: UserShortcutLinkInput,
  ): Promise<UserShortcutLink> {
    return this.request(`/user-shortcut-links/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  deleteUserShortcutLink(id: number): Promise<void> {
    return this.request(`/user-shortcut-links/${id}`, {
      method: 'DELETE',
    });
  }
}
